// @ts-nocheck
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { api } from '../../utils/api.js';

/**
 * CategoryPicker — searchable dropdown for Shopify taxonomy categories.
 * Styled to match EditableCell/InlineEdit focus behavior.
 *
 * Props:
 *   value         — current breadcrumb string (display)
 *   categoryId    — current gid_short (canonical)
 *   onSelect      — (gid_short: string, full_name: string) => void
 *   placeholder   — placeholder text when empty
 *   suggestions   — optional array of {gid_short, full_name} for quick picks
 */
export default function CategoryPicker({
  value,
  categoryId,
  onSelect,
  placeholder = '',
  suggestions = [],
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(0);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  // Debounced search
  const doSearch = useCallback((q) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q || q.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await api.get(`/taxonomy/search?q=${encodeURIComponent(q)}&limit=25`);
        setResults(res.results || []);
        setHighlightIdx(0);
      } catch (e) {
        setResults([]);
      }
      setLoading(false);
    }, 200);
  }, []);

  useEffect(() => {
    doSearch(query);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, doSearch]);

  // Close on outside click or window blur
  useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    }
    function handleBlur() {
      setOpen(false);
      setQuery('');
    }
    document.addEventListener('mousedown', handleClick);
    window.addEventListener('blur', handleBlur);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      window.removeEventListener('blur', handleBlur);
    };
  }, [open]);

  // Focus input when opened
  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  function handleSelect(item) {
    onSelect(item.gid_short, item.full_name);
    setOpen(false);
    setQuery('');
  }

  function handleKeyDown(e) {
    const items = displayItems();
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx(i => Math.min(i + 1, items.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && items.length > 0) {
      e.preventDefault();
      handleSelect(items[highlightIdx]);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
    }
  }

  // Get the leaf node from a breadcrumb
  function getLeaf(fullName) {
    if (!fullName) return '';
    const parts = fullName.split(' > ');
    return parts[parts.length - 1];
  }

  // Format breadcrumb in dropdown results — muted prefix, bold leaf
  function renderBreadcrumb(fullName) {
    const parts = fullName.split(' > ');
    if (parts.length <= 1) return <span style={{ fontWeight: 500 }}>{fullName}</span>;
    const leaf = parts[parts.length - 1];
    const prefix = parts.slice(0, -1).join(' > ');
    return (
      <span>
        <span style={{ color: 'var(--color-text-3)', fontSize: 12 }}>{prefix} &rsaquo; </span>
        <span style={{ fontWeight: 500 }}>{leaf}</span>
      </span>
    );
  }

  // What to show in the dropdown
  function displayItems() {
    if (query.length >= 2) return results;
    if (suggestions.length > 0) return suggestions;
    return [];
  }

  const items = displayItems();
  const showDropdown = open && (items.length > 0 || loading || query.length >= 2);

  // Single DOM tree — idle text and search input always mounted, toggled via visibility.
  // Eliminates the mount/unmount flash when transitioning between states.
  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      {/* Idle display — visible when closed, hidden when open */}
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 4,
          cursor: 'pointer', minHeight: 28,
          padding: '3px 6px', fontSize: 13,
          border: '1px solid transparent',
          borderRadius: 'var(--r-sm)',
          background: 'transparent',
          color: value ? 'var(--color-text)' : 'var(--color-text-3)',
          width: '100%', overflow: 'hidden', boxSizing: 'border-box',
          visibility: open ? 'hidden' : 'visible',
        }}
        onClick={e => { e.stopPropagation(); setOpen(true); }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
          {value ? getLeaf(value) : placeholder}
        </span>
      </div>

      {/* Search input — absolutely positioned over idle text when open */}
      {open && (
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onClick={e => e.stopPropagation()}
          placeholder="Search categories..."
          className="ie"
          style={{
            position: 'absolute', top: 0, left: 0, right: 0,
            display: 'block', width: '100%', fontSize: 13,
            border: '1.5px solid var(--color-primary)',
            borderRadius: 'var(--r-sm)',
            padding: '3px 6px',
            background: 'var(--color-surface)',
            color: 'var(--color-text)',
            outline: 'none', boxSizing: 'border-box',
          }}
        />
      )}

      {/* Dropdown results */}
      {showDropdown && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, minWidth: 340, zIndex: 200,
          marginTop: 4, maxHeight: 320, overflowY: 'auto',
          background: 'var(--color-surface)', border: '1px solid var(--color-border)',
          borderRadius: 'var(--r-sm)', boxShadow: 'var(--shadow-card)',
        }}>
          {query.length < 2 && suggestions.length > 0 && (
            <div style={{ padding: '6px 12px 4px', fontSize: 11, fontWeight: 600, color: 'var(--color-text-3)', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
              Suggested
            </div>
          )}
          {loading && (
            <div style={{ padding: '10px 12px', fontSize: 12, color: 'var(--color-text-3)' }}>
              Searching...
            </div>
          )}
          {!loading && query.length >= 2 && results.length === 0 && (
            <div style={{ padding: '10px 12px', fontSize: 12, color: 'var(--color-text-3)' }}>
              No categories found
            </div>
          )}
          {items.map((item, idx) => (
            <button
              key={item.gid_short}
              onClick={() => handleSelect(item)}
              onMouseEnter={() => setHighlightIdx(idx)}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: '7px 12px', fontSize: 13, cursor: 'pointer',
                border: 'none', borderBottom: idx < items.length - 1 ? '1px solid var(--color-border)' : 'none',
                background: idx === highlightIdx ? 'var(--color-surface-2)' : 'transparent',
                color: 'var(--color-text)',
                lineHeight: 1.4,
              }}
            >
              {renderBreadcrumb(item.full_name)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
